import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { paymentAPI, PaymentRecord, PaymentStatus, PaginationMeta } from '../../services/subscriptionApi';

// ─── Types ────────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | PaymentStatus;

interface OrderHistoryPageProps {
  userId?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

function getStatusBadgeVariant(status: PaymentStatus): 'warning' | 'success' | 'danger' | 'secondary' {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'secondary';
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrderHistoryPage = ({ userId: propUserId }: OrderHistoryPageProps) => {
  // userId is resolved from auth token server-side; prop is accepted for future use
  const { user } = useAuth();
  void (propUserId ?? user?.id); // acknowledged, used server-side via JWT

  // ── State ──────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async (page: number, filter: StatusFilter) => {
    try {
      setLoading(true);
      setError(null);

      const params: { page: number; limit: number; status?: PaymentStatus } = {
        page,
        limit: pagination.limit,
      };
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await paymentAPI.getUserPayments(params);
      setOrders(response.data.payments);
      setPagination(response.data.pagination);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load order history.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchOrders(1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    // fetchOrders will be triggered by the useEffect above
  };

  const handlePageChange = (newPage: number) => {
    fetchOrders(newPage, statusFilter);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Order History</h1>
        <p className="text-gray-600 mt-1">View all your subscription orders and their payment status.</p>
      </div>

      {/* ── Task 16.3: Status filter tabs ─────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map(({ label, value }) => (
          <button
            key={value}
            onClick={() => handleFilterChange(value)}
            className={[
              'px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
              statusFilter === value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Loading state ──────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <LoadingSpinner size="xl" />
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        </div>
      )}

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div className="text-center py-12">
          <div className="text-red-500 text-4xl mb-3">⚠️</div>
          <p className="text-gray-700 font-medium">Unable to load orders</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4"
            onClick={() => fetchOrders(pagination.page, statusFilter)}
          >
            Retry
          </Button>
        </div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {!loading && !error && orders.length === 0 && (
        <div className="text-center py-16">
          <div className="text-gray-300 text-6xl mb-4">📋</div>
          <p className="text-gray-600 font-medium">No orders found</p>
          <p className="text-gray-400 text-sm mt-1">
            {statusFilter === 'all'
              ? "You haven't placed any orders yet."
              : `No ${statusFilter} orders found.`}
          </p>
        </div>
      )}

      {/* ── Task 16.2: Order list ──────────────────────────────────────────── */}
      {!loading && !error && orders.length > 0 && (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} variant="default">
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">

                  {/* Left: order details */}
                  <div className="flex-1 space-y-2">
                    {/* Order ID + Status badge */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                        {order.orderId}
                      </span>
                      <Badge variant={getStatusBadgeVariant(order.status)}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </Badge>
                    </div>

                    {/* Plan name */}
                    <p className="text-base font-semibold text-gray-800">{order.planName}</p>

                    {/* Transaction ID */}
                    <p className="text-sm text-gray-500">
                      <span className="font-medium text-gray-600">Transaction ID:</span>{' '}
                      <span className="font-mono">{order.transactionId}</span>
                    </p>
                  </div>

                  {/* Right: amount + date */}
                  <div className="sm:text-right space-y-1 shrink-0">
                    <p className="text-lg font-bold text-gray-900">
                      ₹{order.amount.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(order.submittedAt)}</p>
                  </div>

                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Task 16.1: Pagination controls ────────────────────────────────── */}
      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            ← Previous
          </Button>

          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
            <span className="ml-2 text-gray-400">({pagination.total} total)</span>
          </span>

          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next →
          </Button>
        </div>
      )}

    </div>
  );
};

export default OrderHistoryPage;
